/**
 * Clay's block reason → the words a rep reads (moved out of
 * `dispatch-rep-status.ts` unchanged — review-fix U14, F1; that module, the
 * ONE classifier, uses these). DOM-free and fetch-free.
 */
import { z } from 'zod';

import { safeParseJson } from '../_shared/parse-json';
import {
  BLOCK_PREFIXES,
  matchStopUnsafeDeliveryReason,
} from './dispatch-inspector-logic';

// ── Plain block reasons (ported from V3's `Post final status alert` node) ────
//
// `BLOCK_PREFIXES` and `FINAL_STATUS_BLOCK_NODE` used to be declared here.
// They moved to `dispatch-inspector-logic.ts` (2026-09-30, sharing rules (m))
// so the retry's eligibility test and this classifier read ONE table: the
// seven reasons this file turns into rep words are exactly the seven that
// prove a refusal happened before delivery, and a copy of the table in each
// module could drift those two answers apart.
//
// This file reads that table's `repText` column ONLY (U4). The other two
// columns — `prefix` and `v3Phrase` — are the identity of V3's
// `Stop unsafe delivery` node and belong to `isStopUnsafeDeliveryReason`,
// which decides whether a real prospect may be emailed again. Rewording
// `repText` here can never move that answer.

/** A block reason split into the cause a rep reads and Clay's optional `Findings:` tail. */
export interface BlockReasonView {
  /** One plain sentence (sometimes two clauses) naming the cause. Never empty. */
  headline: string;
  /** Clay's copy-QA findings, cleaned and capped — `null` when it sent none. */
  findings: string | null;
}

/** Clay's copy-QA gate appends its evidence after this literal. */
const FINDINGS_MARKER = ' Findings:';

/** ~300 chars of findings is what fits the checklist row without becoming a wall. */
const FINDINGS_MAX = 300;

/**
 * Strip anything that is markup rather than words: HTML tags (Clay stores raw
 * `HTMLBody` fragments in its findings) and the square brackets around its
 * machine labels. Deliberately a local 3-line regex, not an import of either
 * private `stripHtml` in `src/` or `web/` — `mini-apps/**` may not reach into
 * those. Whitespace is collapsed so a multi-line fragment reads as one line.
 */
function sanitizeFragment(text: string): string {
  return text
    .replace(/<[^>]+>/g, ' ')
    .replace(/[[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Drop an em-dash clause that a LATER clause restates with more detail (Clay
 * joins its generic gate name to its specific finding: "copy QA flagged the
 * copy — copy QA flagged step(s) 5"), and drop Clay's own outcome boilerplate
 * ("— nothing was sent. Fix the copy in …"), which Terra's own banner already
 * says. What survives is the CAUSE; Terra owns the remediation sentence. [R20]
 */
function dedupeClauses(text: string): string {
  const clauses = text
    .split(/\s+—\s+/)
    .map((clause) => clause.trim())
    .filter((clause) => clause !== '' && !/^nothing was sent\b/i.test(clause));
  const leadWords = (clause: string) => clause.toLowerCase().split(/\s+/).slice(0, 3).join(' ');
  const kept = clauses.filter((clause, i) =>
    !clauses.some((later, j) => j > i && (later.toLowerCase() === clause.toLowerCase() || leadWords(later) === leadWords(clause))),
  );
  return kept.join(' — ');
}

/** Does this fragment contain a word at all, or did it clean down to punctuation and digits? */
function hasRealWords(fragment: string): boolean {
  return /[A-Za-z]{3}/.test(fragment);
}

/**
 * `[launcher.dispatch]` — a bracketed dotted lowercase namespace tag.
 * Deliberately NOT "any bracketed word": a real copy-QA finding carries
 * `[internal_scaffolding]`, which (l) renders as "internal scaffolding". Only
 * the DOT makes it a machine namespace. Tested against the RAW fragment,
 * because `sanitizeFragment` has already removed the brackets by the time a
 * cleaned fragment exists.
 */
const NAMESPACE_TAG = /\[[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+\]/;

/** A `clay_*` machine word. Raw-only for the same reason: the `_` pass spaces it out. */
const CLAY_MACHINE_WORD = /\bclay_\w+/i;

/**
 * A Salesforce record id: a run of exactly 15 or 18 letters and digits
 * containing at least one digit (`005ABCDEF123456789`). The digit is what
 * keeps a long ordinary word — "internationalization" — out of the net.
 */
const RECORD_ID = /(?<![A-Za-z0-9])(?=[A-Za-z0-9]*\d)[A-Za-z0-9]{15}(?:[A-Za-z0-9]{3})?(?![A-Za-z0-9])/;

/**
 * A 100–599 number that is not one of Clay's own step numbers. Clay's copy-QA
 * node says "step(s) 500" and (l) renders that faithfully — its step numbering
 * is the ONE legitimate number in these strings. Every other three-digit
 * number in this range is a status code as far as a rep is concerned.
 */
function hasStatusShapedNumber(fragment: string): boolean {
  for (const match of fragment.matchAll(/(?<![0-9])[1-5][0-9]{2}(?![0-9])/g)) {
    if (/\bsteps?(?:\(s\))?\s*$/i.test(fragment.slice(0, match.index))) continue;
    return true;
  }
  return false;
}

/**
 * Does this fragment still carry internal machinery after cleaning? — the
 * things R20 is absolute a rep never reads: a `clay_*` word, an HTTP status,
 * a record id, a `[dotted.namespace]` tag. (l)'s "Internal machinery" bullet.
 *
 * Takes BOTH spellings of the same fragment because the two halves survive in
 * different places: brackets and underscores only exist in `raw` (the cleaning
 * pass strips the first and spaces out the second), while the status word and
 * the number survive into `cleaned`. Checking only one of the two is exactly
 * the gap that let `[launcher.dispatch]` and `HTTP 429` reach a rep.
 *
 * This REPLACES the old `isRepReadableReason` status-number check and widens
 * it: the same test now runs on a known phrase's detail clause and on the
 * `Findings:` tail, neither of which was ever checked. (m)
 */
function hasProhibitedInternals(raw: string, cleaned: string): boolean {
  if (NAMESPACE_TAG.test(raw) || CLAY_MACHINE_WORD.test(raw)) return true;
  return [raw, cleaned].some(
    (fragment) => /\bhttps?\b/i.test(fragment) || RECORD_ID.test(fragment) || hasStatusShapedNumber(fragment),
  );
}

/**
 * Keep the clauses of an already-cleaned KNOWN headline that still read as
 * prose; drop the ones carrying internal machinery. Returns `null` when
 * nothing survives, and the caller then shows Clay's own plain phrase alone.
 *
 * A known reason is never downgraded to a crash for this: `Stop unsafe
 * delivery` PROVED nothing was sent, and telling a rep "crashed" instead of
 * "stopped" would be the worse lie. The phrase survives; the detail goes. (m)
 */
function keepReadableClauses(headline: string, raw: string): string | null {
  const kept = headline.split(/\s+—\s+/).filter((clause) => !hasProhibitedInternals(raw, clause));
  return kept.length === 0 ? null : kept.join(' — ');
}

/**
 * A block reason as a rep should read it: a known prefix → V3's plain words;
 * an unknown `word_like_this:` prefix → the text after it; anything else as
 * given. Markup and square brackets go, repeated clauses go, a trailing
 * ` — some_machine_token: …` restatement (the projection joins
 * `reason — technical_reason`) is dropped, and any leftover snake-case is
 * spaced out — so no `_` word, no tag and no bracketed label reaches a rep.
 * An unrecognised reason that does not read as prose, or that still carries
 * internal machinery, is treated as NO reason at all ({@link hasRealWords},
 * {@link hasProhibitedInternals}). Clay's `Findings:` tail, when there is one,
 * is returned separately — and is checked the same way. [R20]
 *
 * THREE rep-bound fragments, three applications of the same check (U5, (m)):
 * the unknown-reason headline (rejected → `null` → the row is a crash), the
 * detail clause after a recognised phrase (dropped, the phrase stays and the
 * row stays a stop), and the findings tail (dropped → the Copy QA row falls
 * back to the headline). A known phrase at the FRONT used to mark the whole
 * cleaned head readable, and the tail was never checked at all.
 */
export function describeBlockReason(raw: string | null | undefined): BlockReasonView | null {
  const text = (raw ?? '').trim();
  if (text === '') return null;

  const split = text.indexOf(FINDINGS_MARKER);
  const rawHead = split === -1 ? text : text.slice(0, split);
  const rawTail = split === -1 ? null : text.slice(split + FINDINGS_MARKER.length);
  const head = sanitizeFragment(rawHead);
  const tail = rawTail === null ? null : sanitizeFragment(rawTail);
  const capped = tail === null ? '' : tail.replace(/_/g, ' ').slice(0, FINDINGS_MAX).trim();
  const findingsText =
    capped === '' || (rawTail !== null && hasProhibitedInternals(rawTail, capped)) ? null : capped;

  const described = (() => {
    for (const phrase of BLOCK_PREFIXES) {
      if (head.startsWith(phrase.prefix)) return { headline: phrase.repText, known: true };
    }
    const prefixed = /^[a-z][a-z0-9_]*:\s*(.+)$/.exec(head);
    const body = prefixed ? prefixed[1] : head;
    // A real trailing `snake_case_token: <anything>` restatement — the narrow
    // charset this used to match never fired on prod reasons, whose technical
    // tail carries spaces, commas and an email address.
    const withoutTail = body.replace(/\s+—\s+[a-z][a-z0-9]*(?:_[a-z0-9]+)+:.*$/i, '');
    const headline = dedupeClauses(withoutTail.replace(/_/g, ' ')).trim();
    // A reason V3's own alert node already plain-ified (what every real prod
    // sender refusal carries) is a KNOWN reason too, even though it arrives
    // without its machine prefix. The exemption is the PHRASE's, not the free
    // text Clay appended after it.
    const phrase = matchStopUnsafeDeliveryReason(head);
    if (phrase === null) return { headline, known: false };
    return { headline: keepReadableClauses(headline, rawHead) ?? phrase.repText, known: true };
  })();

  if (described.headline === '') return null;
  if (!described.known && !hasRealWords(described.headline)) return null;
  if (!described.known && hasProhibitedInternals(rawHead, described.headline)) return null;
  return { headline: described.headline, findings: findingsText };
}

/** Just the cause sentence — the shape every pre-(l) caller wants. */
export function plainBlockReason(raw: string | null | undefined): string | null {
  return describeBlockReason(raw)?.headline ?? null;
}

// ── A Clay step that ERRORED (PR 1 U2) ───────────────────────────────────────
//
// `describeBlockReason` above is for a STOP — Clay's own prose, with an
// all-or-nothing safety check (anything carrying machinery is "no reason", and
// the row falls to a crash). A routine item that THREW is the opposite
// shape: the stored reason is Clay's raw tool error, always machine text (a
// JSON envelope, an error code, a record id), and the rep still has to decide
// whether to retry it. So this half REDACTS instead of rejecting, and also
// says WHICH SIDE OF DELIVERY the failure fell on when Terra can tell.
//
// What Terra can tell is narrow, and learned from Clay's real error text
// (production `launcher_dispatch_log`, 2026-10-19): the message names a Clay
// FUNCTION COLUMN ("Function column \"Create SA Record\"") or a missing input
// field, never a V3 node. Two tables decide the phase; everything outside them
// is `unknown`, which the confirm words as the plain hedge. Never guess.

/** Which side of the delivery step a failed Clay step is on. */
export type ClayFailurePhase = 'before_delivery' | 'delivery' | 'after_delivery' | 'unknown';
type KnownPhase = Exclude<ClayFailurePhase, 'unknown'>;

/**
 * V3 node → phase, PINNED from `clay workflows graph get wf_0tlf6uoWTASuf9eDC3g
 * --mode full` (2026-10-19; v10). Order in the graph: `Route delivery channel`
 * → (`Check existing Gong flows` → … → `Clear to enroll in Gong?`) →
 * `Enroll contact in Gong flow` | `Send through Inflection` → the success check
 * → `Write Gong enrollment to action queue` → `Update Campaign Member and
 * handoff timing` → `Delivery complete`. Only the nodes a failure could name
 * and that matter for the rep's decision are listed; a renamed node falls out
 * of the table and degrades to `unknown`.
 *
 * Re-pin when V3 gains or renames a delivery-side node.
 */
export const V3_NODE_PHASES: Readonly<Record<string, KnownPhase>> = {
  'Qualify and route contact': 'before_delivery',
  'Validate recipient email': 'before_delivery',
  'Standardize first and company name': 'before_delivery',
  'Email QA Remediation': 'before_delivery',
  'Check existing Gong flows': 'before_delivery',
  'Skip duplicate Gong enrollment': 'before_delivery',
  'Evaluate Gong flow membership': 'before_delivery',
  'Send through Inflection': 'delivery',
  'Enroll contact in Gong flow': 'delivery',
  'Write Gong enrollment to action queue': 'after_delivery',
  'Update Campaign Member and handoff timing': 'after_delivery',
};

/**
 * Clay FUNCTION COLUMN → phase — the only step name Clay's own error text
 * carries. ASSUMED, corroborated not proven: `Create SA Record` writes the
 * Salesforce record keyed by `Terra_External_ID__c` (`{{External ID}}_{{Engage
 * Flow Instance ID}}`), which only exists once the person is enrolled, and the
 * V3 node that calls it is `Write Gong enrollment to action queue` (tool `Add
 * to Action Queue`). A function column Terra has not seen is `unknown`.
 */
export const CLAY_FAILURE_PHASE_BY_NAME: Readonly<Record<string, KnownPhase>> = {
  'Create SA Record': 'after_delivery',
};

/**
 * A missing function input → the V3 node whose function the input belongs to.
 * `ERROR_MISSING_INPUT` means that function REFUSED to run, so a delivery node's
 * own missing input is a failure before delivery. Verified against the graph:
 * `Recipient First Name` is an input of `Send through Inflection`.
 */
const MISSING_INPUT_STEP: Readonly<Record<string, string>> = {
  'Recipient First Name': 'Send through Inflection',
};

/**
 * Final-status stops that are NOT one of the seven proven-safe refusals: the
 * delivery step ran and Clay could not confirm the outcome. The DELIVERY phase.
 */
const DELIVERY_UNCONFIRMED_REASONS: readonly string[] = [
  'Gong returned no enrollment id',
  'Inflection returned no success response',
];

/** What a rep reads about a failed Clay step, and where it fell. */
export interface ClayFailureView {
  /** One plain, redacted sentence, ≤ {@link CLAY_FAILURE_REASON_MAX} chars; `null` when Clay gave nothing readable. */
  reason: string | null;
  /** The step Clay (or the V3 table) names, or `null`. */
  step: string | null;
  phase: ClayFailurePhase;
}

/** ~240 chars is a sentence or two in a confirm dialog without becoming a wall. */
export const CLAY_FAILURE_REASON_MAX = 240;

const ToolErrorEnvelopeSchema = z.object({ message: z.string() });
const NestedErrorsSchema = z.array(z.object({ message: z.string() }));

const MACHINERY_PREFIX = 'Tool execution failed with status:';

/** Undo the JSON string escapes a tolerant scan leaves behind. */
function unescapeJsonText(text: string): string {
  return text
    .replace(/\\u([0-9a-fA-F]{4})/g, (_m, hex: string) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/\\[nrt]/g, ' ')
    .replace(/\\(["\\/])/g, '$1');
}

/** Does `text` open like a JSON object or array (so it is syntax, not prose)? */
function looksLikeJson(text: string): boolean {
  return /^\s*(?:\{|\[\s*(?:\{|\]))/.test(text);
}

/**
 * Every `"message":"…"` string in `text`, found by a TOLERANT scan rather than
 * `JSON.parse`: Clay's projection caps a stored reason at 2,048 characters, so
 * a long envelope arrives cut mid-string and no parser accepts it. `truncated`
 * is true when the LAST string never closed (the cut fell inside it).
 */
function scanMessages(text: string): { messages: string[]; truncated: boolean } {
  const messages: string[] = [];
  let truncated = false;
  for (const match of text.matchAll(/"(?:message|error|reason|detail|description)"\s*:\s*"((?:[^"\\]|\\.)*)("?)/g)) {
    messages.push(unescapeJsonText(match[1]));
    truncated = match[2] === '';
  }
  return { messages, truncated };
}

/** Drop the half-word a cut left at the end. */
function dropPartialWord(text: string): string {
  return /[\s.!?:;,)]$/.test(text) ? text : text.replace(/\s*\S*$/, '');
}

/**
 * Replace any JSON in `text` with its human messages. The first `{"` / `[{"`
 * opener starts the JSON; what precedes it is prose and is kept, what follows is
 * reduced to its message strings by the tolerant scan (so a cut or invalid
 * envelope behaves like a whole one), and keys, braces, quotes and error codes
 * are never shown. JSON with no readable message yields nothing.
 */
function unpackJson(text: string): { text: string; truncated: boolean; hadJson: boolean } {
  const opener = text.search(/[{[]\s*["{\]]|[{[]\s*$/);
  if (opener === -1) {
    return looksLikeJson(text)
      ? { text: '', truncated: false, hadJson: true }
      : { text, truncated: false, hadJson: false };
  }
  const head = text.slice(0, opener).replace(/[\s:—-]+$/, '');
  const { messages, truncated } = scanMessages(text.slice(opener));
  let tail = messages.join('; ');
  if (truncated) tail = dropPartialWord(tail);
  return { text: [head, tail].filter((part) => part !== '').join(': '), truncated, hadJson: true };
}

/**
 * Clay stores its tool error as `{"message":"…"}`; unwrap it. A whole envelope
 * goes through the schema; a truncated or invalid one through the tolerant
 * scan. JSON syntax with no human message in it yields nothing, never itself.
 */
function unwrapMessage(text: string): { text: string; truncated: boolean } {
  const envelope = safeParseJson(ToolErrorEnvelopeSchema, text);
  if (envelope.success) return { text: envelope.data.message, truncated: false };
  if (!looksLikeJson(text)) return { text, truncated: false };
  const { messages, truncated } = scanMessages(text);
  return { text: messages[0] ?? '', truncated };
}

/** A record id with the `with id:` words that introduce it, so no dangling label survives. */
const LABELLED_RECORD_ID = new RegExp(`(?:\\s+with)?\\s*\\bids?:?\\s*${RECORD_ID.source}`, 'gi');
const BARE_RECORD_ID = new RegExp(RECORD_ID.source, 'g');

/**
 * Make Clay's tool error safe to read: drop what R20 forbids a rep seeing
 * (record ids, HTTP words and status-shaped numbers, `clay_*` tags, brackets,
 * the Salesforce `__c` suffix, underscores) and keep the rest. The opposite of
 * `describeBlockReason`'s reject-on-contact: a tool error is always machine
 * text, so rejecting would leave the rep nothing at all.
 */
function redactToolError(text: string): string {
  return text
    .replace(LABELLED_RECORD_ID, '')
    .replace(BARE_RECORD_ID, '')
    .replace(/\bHTTP\s*[0-9]{3}\b/gi, '')
    .replace(/\bclay_\w+/gi, '')
    // Clay's own run / workflow ids (`wfr_…`, `rr_…`, `run_…`) and a bracketed
    // dotted namespace tag (`[launcher.dispatch]`) are internal machinery.
    .replace(/\b(?:wfr|rr|run|wf|wfn)_[A-Za-z0-9]+/g, '')
    .replace(/\[[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+\]/g, '')
    .replace(/__c\b/g, '')
    .replace(/(?<![0-9])[1-5][0-9]{2}(?![0-9])/g, (match, offset: number) =>
      /\bsteps?(?:\(s\))?\s*$/i.test(text.slice(0, offset)) ? match : '',
    )
    .replace(/[{}[\]]/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\(\s*\)/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([:;,.])/g, '$1')
    .replace(/[\s:;,—-]+$/, '')
    .trim();
}

/** The first quoted name in `text` that either phase table knows. */
function namedStep(text: string): { step: string; phase: KnownPhase } | null {
  for (const match of text.matchAll(/["']([^"']+)["']/g)) {
    const name = match[1].trim();
    const phase = CLAY_FAILURE_PHASE_BY_NAME[name] ?? V3_NODE_PHASES[name];
    if (phase !== undefined) return { step: name, phase };
  }
  return null;
}

/**
 * A Clay tool error as a rep reads it: the redacted reason, the step when
 * known, and the phase when the tables can place it. Never throws; every
 * unreadable or absent input is `{ reason: null, step: null, phase: 'unknown' }`.
 */
export function describeClayFailure(raw: string | null | undefined): ClayFailureView {
  const text = (raw ?? '').trim();
  const empty: ClayFailureView = { reason: null, step: null, phase: 'unknown' };
  if (text === '') return empty;

  const unwrapped = unwrapMessage(text);
  const message = unwrapped.text.trim();
  // A message cut off inside its own text (not inside the machinery prefix).
  if (MACHINERY_PREFIX.startsWith(message) || message.startsWith(MACHINERY_PREFIX.slice(0, message.length)) && message.length < MACHINERY_PREFIX.length) return empty;
  const truncated = unwrapped.truncated;
  // The envelope's machinery prefix goes with or without a separator after it;
  // what follows (the useful message) stays.
  const body = message.replace(/^Tool execution failed with status:\s*[A-Z_]+\b\s*[—:-]?\s*/, '').trim();
  if (body === '') return empty;

  let reasonText = body;
  let step: string | null = null;
  let phase: ClayFailurePhase = 'unknown';

  const missing = /Missing required function input fields?:\s*((?:'[^']*'\s*,?\s*)+)$/i.exec(body);
  const column = /Function column "([^"]+)":\s*(.*)$/s.exec(body);
  if (missing !== null) {
    const fields = missing[1].split(',').map((field) => field.replace(/^[\s'"]+|[\s'".]+$/g, '')).filter((f) => f !== '');
    reasonText = fields.length === 0 ? 'a required value was missing' : `a required value was missing (${fields.join(', ')})`;
    const node = fields.map((field) => MISSING_INPUT_STEP[field]).find((name) => name !== undefined);
    if (node !== undefined) {
      step = node;
      phase = 'before_delivery';
    }
  } else if (/Missing required function input fields?:/i.test(body)) {
    // A field list the cut left unterminated: say nothing about WHICH field.
    reasonText = 'a required value was missing';
  } else if (/^Please include all inputs\.?$/i.test(body)) {
    reasonText = 'a required value was missing';
  } else if (column !== null) {
    step = column[1];
    phase = CLAY_FAILURE_PHASE_BY_NAME[step] ?? V3_NODE_PHASES[step] ?? 'unknown';
    const nested = safeParseJson(NestedErrorsSchema, column[2]);
    if (nested.success) {
      reasonText = nested.data.map((entry) => entry.message).join('; ');
    } else {
      const unpacked = unpackJson(column[2]);
      // JSON drops its own half-word; plain text takes the outer cut's.
      reasonText = !unpacked.hadJson && truncated ? dropPartialWord(unpacked.text) : unpacked.text;
    }
  } else if (/Function column(?:\s+"[^"]*"?)?\s*:?\s*$/.test(body)) {
    // The cut fell inside the column name itself: nothing readable follows.
    reasonText = '';
  } else {
    const unpacked = unpackJson(body);
    reasonText = !unpacked.hadJson && truncated ? dropPartialWord(unpacked.text) : unpacked.text;
    const named = namedStep(message);
    if (named !== null) {
      step = named.step;
      phase = named.phase;
    } else if (DELIVERY_UNCONFIRMED_REASONS.some((reason) => body.startsWith(reason))) {
      phase = 'delivery';
    }
  }

  const cleaned = redactToolError(reasonText).slice(0, CLAY_FAILURE_REASON_MAX).trim();
  // The step NAME is Clay's text too: it gets the same redaction as the reason
  // (the phase above was already decided from the raw name).
  const shownStep = step === null ? null : redactToolError(step);
  return {
    reason: hasRealWords(cleaned) ? cleaned : null,
    step: shownStep !== null && hasRealWords(shownStep) ? shownStep : null,
    phase,
  };
}

/** Just the redacted reason — the shape a banner wants. */
export function plainClayFailureReason(raw: string | null | undefined): string | null {
  return describeClayFailure(raw).reason;
}
